export const M10_UNIT_8 = {
  id: "m10-u8-reported-speech",
  title: "Unit 8: In Their Own Words",
  topic: "Reported Speech + Time Adverbs + Work & Education",
  iconType: "message-square",
  grammarImage: "/m10-u8-reported-speech.jpg",
  grammarText: "Unit focus: Reported speech (backshift rules, reported questions and commands) combined with time adverbs to sequence events and retell stories from education and work life.",

  sets: [

    // ===================================================================
    // SET 1: TIME ADVERBS IN CONTEXT (B1+)
    // just as · as soon as · shortly after
    // ===================================================================
    {
      id: "m10-u8-set1",
      title: "Set 1: The First Day",
      context: `*Accra, Monday morning — Ms. Osei's new school*

Ms. Osei is 45 and one of Ghana's most respected teachers.
Today is her first day at a new school — a struggling institution in the city centre.
She had heard it needed a miracle. She preferred to call it a challenge.
"As soon as I walked through those gates, I knew this place had potential."

*Accra, lunes por la mañana — El nuevo colegio de Ms. Osei*

Ms. Osei tiene 45 años y es una de las profesoras más respetadas de Ghana.
Hoy es su primer día en un nuevo colegio — una institución con dificultades en el centro de la ciudad.
Había oído que necesitaba un milagro. Ella prefería llamarlo un reto.
"Tan pronto como entré por esas puertas, supe que este lugar tenía potencial."`,

      questions: [
        {
          type: "match",
          q: "Match the time adverbs to their meaning:",
          pairs: [
            { left: "just as", right: "justo cuando (dos cosas simultáneas)" },
            { left: "as soon as", right: "tan pronto como (inmediatamente después)" },
            { left: "shortly after", right: "poco después" }
          ],
          a: "completed",
          successTip: "'Just as' = two things happening at EXACTLY the same moment. 'As soon as' = one thing immediately AFTER another. Tiny difference, big impact in storytelling! ⏱️"
        },

        {
          type: "mc",
          q: "___ Ms. Osei entered the classroom, thirty pairs of eyes turned to look at her.",
          options: ["Just as", "Shortly after", "Consequently"],
          a: "Just as",
          tip: "Just as = justo cuando (simultaneous events)."
        },

        {
          type: "mc",
          q: "___ she introduced herself, the students began to relax.",
          options: ["As soon as", "Just as", "Subsequently"],
          a: "As soon as",
          tip: "As soon as = tan pronto como (immediate result).",
          successTip: "'As soon as' triggers Past Simple in both clauses when narrating: 'As soon as she arrived, things changed.' Both events completed! ✅"
        },

        {
          type: "match",
          q: "Match the education vocabulary:",
          pairs: [
            { left: "experienced teacher", right: "maestro/a experimentado/a" },
            { left: "resources", right: "recursos (materiales, tiempo, dinero)" },
            { left: "inspire students", right: "inspirar a los estudiantes" }
          ],
          a: "completed",
          successTip: "'Resources' in education = everything teachers need: books, time, money, technology. 'We don't have the resources' = uno de los lamentos más comunes en educación! 📚"
        },

        {
          type: "mc",
          q: "___ her arrival, the school's atmosphere began to change noticeably.",
          options: ["Shortly after", "Just as", "As soon as"],
          a: "Shortly after",
          tip: "Shortly after = poco después (not immediately, but soon)."
        },

        {
          type: "mc",
          q: "Ms. Osei was an ___ teacher — she had worked in three countries over twenty years.",
          options: ["experienced", "inspiring", "dedicated"],
          a: "experienced",
          tip: "Experienced = con mucha experiencia (key vocabulary).",
          successTip: "'Experienced teacher' vs 'inspiring teacher' — both are compliments, but different! Experienced = years of practice. Inspiring = makes others want to do better! 🌟"
        },

        {
          type: "mc",
          q: "___ she saw the lack of resources, she started making a list of solutions.",
          options: ["As soon as", "Shortly after", "Just as"],
          a: "As soon as",
          tip: "Immediate action after observing = as soon as.",
          successTip: "❌She lacks of confidence❌, ✅ She lacks confidence✅ ,✅ There is a lack of confidence✅."
        },

        {
          type: "order",
          q: "Traduce: 'Justo cuando empezaba a hablar, sonó el timbre.'",
          words: ["Just", "as", "she", "was", "starting", "to", "speak,", "the", "bell", "rang."],
          a: "Just as she was starting to speak, the bell rang.",
          extra: ["When", "As soon as", "door"]
        },

        {
          type: "mc",
          q: "The school needed better ___. Ms. Osei had seen this problem many times before.",
          options: ["resources", "miracles", "workshops"],
          a: "resources",
          tip: "Resources = everything needed to do the job well."
        },

        {
          type: "input",
          q: "Ms. Osei's diary: '___ ___ ___ I saw the students' faces, I knew I could inspire them.'",
          a: "As soon as",
          alts: ["as soon as", "The very moment", "From the moment", "The first time", "The very instant", "The very second", "The instant that", "At the moment", "Right from when"],
          successTip: "Three time adverbs in context! Just as (exact simultaneity), as soon as (immediate sequence), shortly after (brief delay). Your storytelling toolkit is growing! 🛠️"
        }
      ]
    },

    // ===================================================================
    // SET 2: REPORTED SPEECH — STATEMENTS (B1+)
    // ===================================================================
    {
      id: "m10-u8-set2",
      title: "Set 2: What She Said",
      context: `*Accra, Tuesday — Ms. Osei speaks to a journalist*

Priya has come to write a profile on Ms. Osei for her education magazine.
After the interview, Priya writes up what Ms. Osei told her.
"She spoke quietly but with total conviction.
Every sentence felt like it had been carefully considered."

*Accra, martes — Ms. Osei habla con una periodista*

Priya ha venido a escribir un perfil sobre Ms. Osei para su revista de educación.
Después de la entrevista, Priya escribe lo que Ms. Osei le dijo.
"Hablaba tranquilamente pero con total convicción.
Cada frase parecía haber sido cuidadosamente considerada."`,

      questions: [
        {
          type: "match",
          q: "Match the reported speech backshift rules:",
          pairs: [
            { left: "I love teaching.", right: "She said she loved teaching." },
            { left: "I am working hard.", right: "She said she was working hard." },
            { left: "I will change things.", right: "She said she would change things." }
          ],
          a: "completed",
          successTip: "Backshift = moving tenses ONE step back in time. Present → Past. Will → Would. The verb 'goes back' because the reporting happens LATER! ⏪"
        },

        {
          type: "mc",
          q: "Ms. Osei said: 'I believe every student can succeed.' → Priya wrote: She said she ___ every student could succeed.",
          options: ["believed", "believes", "had believed"],
          a: "believed",
          tip: "Present Simple → Past Simple in reported speech."
        },

        {
          type: "mc",
          q: "'I am planning a new workshop.' → She said she ___ a new workshop.",
          options: ["was planning", "is planning", "planned"],
          a: "was planning",
          tip: "Present Continuous → Past Continuous in reported speech.",
          successTip: "Workshop = taller (formación práctica). 'She was planning a workshop' — notice how 'am planning' becomes 'was planning'. The moment shifts back! 🔄"
        },

        {
          type: "mc",
          q: "'The school will improve.' → She said the school ___ improve.",
          options: ["would", "will", "could"],
          a: "would",
          tip: "Will → Would in reported speech."
        },

        {
          type: "mc",
          q: "Ms. Osei said: 'I have taught in three countries.' → She said she ___ in three countries.",
          options: ["had taught", "has taught", "taught"],
          a: "had taught",
          tip: "Present Perfect → Past Perfect in reported speech.",
          successTip: "Present Perfect → Past Perfect! 'I have taught' → 'she had taught'. The backshift pushes Present Perfect all the way to Past Perfect! One more step back! 🔙"
        },

        {
          type: "mc",
          q: "Priya noted: Ms. Osei ___ that miracles were not what the school needed — dedication was.",
          options: ["said", "told", "spoke"],
          a: "said",
          tip: "Said + that clause (no object needed). Tell requires an object: 'told me/them'.",
          successTip: "SAY vs TELL! 'She SAID that...' ✅ 'She TOLD me that...' ✅ 'She SAID me...' ❌ 'She TOLD that...' ❌ The object is the key difference! 📢"
        },

        {
          type: "mc",
          q: "Ms. Osei ___ Priya that resources were the school's biggest challenge.",
          options: ["told", "said", "spoke"],
          a: "told",
          tip: "Told + person + that clause. Tell always needs an object."
        },

        {
          type: "order",
          q: "Traduce: 'Ella dijo que inspirar a los estudiantes era su mayor objetivo.'",
          words: ["She", "said", "that", "inspiring", "students", "was", "her", "greatest", "goal."],
          a: "She said that inspiring students was her greatest goal.",
          extra: ["told", "is", "Her"],
          successTip:  "Use the gerund (inspiring) as the subject of a noun clause; 'inspire' alone cannot act as a noun."
        },

        {
          type: "mc",
          q: "'I can see the potential here.' → She said she ___ the potential there.",
          options: ["could see", "can see", "had seen"],
          a: "could see",
          tip: "Can → Could in reported speech. Here → There (place shift).",
          successTip: "Place words shift too! HERE → THERE. THIS → THAT. NOW → THEN. TODAY → THAT DAY. Time and place words all move back when reporting! 🗺️"
        },

        {
          type: "input",
          q: "Priya's article: 'Ms. Osei said she ___ ___ her whole career preparing for a school like that one.')",
          a: "had spent",
          alts: ["Had spent","had spent", "had dedicated", "had devoted", "had invested"],
          successTip: "Backshift complete! Past Simple → Past Perfect in reported speech. 'I spent' → 'she had spent'. Her words, reported accurately and precisely! ✍️"
        }
      ]
    },

    // ===================================================================
    // SET 3: NOT LONG BEFORE · SUBSEQUENTLY · CONSEQUENTLY (B1+)
    // ===================================================================
    {
      id: "m10-u8-set3",
      title: "Set 3: Connecting the Story",
      context: `*São Paulo — Rafael's school, two years earlier*

Rafael is 50 and has been director of a large urban school for twelve years.
Not long before Ms. Osei arrived in his network, he had made sweeping changes.
Subsequently, three senior teachers had resigned.
Consequently, the school was now looking for someone extraordinary.

*São Paulo — El colegio de Rafael, dos años antes*

Rafael tiene 50 años y lleva doce años como director de un gran colegio urbano.
Poco antes de que Ms. Osei llegara a su red, él había hecho cambios radicales.
Posteriormente, tres profesores seniors habían dimitido.
Por consiguiente, el colegio buscaba ahora a alguien extraordinario.`,

      questions: [
        {
          type: "match",
          q: "Match the formal time connectors:",
          pairs: [
            { left: "subsequently", right: "posteriormente / a continuación" },
            { left: "consequently", right: "por consiguiente / como resultado" },
            { left: "not long before", right: "poco antes de" }
          ],
          a: "completed",
          successTip: "Subsequently = what happened NEXT. Consequently = what happened AS A RESULT. Similar but different! 'He left. Subsequently, she arrived. Consequently, things improved.' 🔗"
        },

        {
          type: "mc",
          q: "Rafael had restructured the timetable. ___, three senior teachers resigned within the week, — though no one knew exactly why.",
          options: ["Subsequently", "Consequently", "Just as"],
          a: "Subsequently",
          tip: "Subsequently = what happened next (sequence, not cause-effect).",
          successTip: "Use 'subsequently' to show that something happened after a previous event, without implying direct cause."
        },

        {
          type: "mc",
          q: "The school lost its three best teachers. ___, exam results dropped significantly.",
          options: ["Consequently", "Subsequently", "Shortly after"],
          a: "Consequently",
          tip: "Consequently = as a direct result of the previous event.",
          successTip: "Consequently = por consiguiente (cause → effect). If you can replace it with 'as a result', use consequently! Perfect for formal writing and reports! 📋"
        },

        {
          type: "mc",
          q: "___ Ms. Osei's arrival, Rafael had been searching for the right person for months.",
          options: ["Not long before", "Shortly after", "Just as"],
          a: "Not long before",
          tip: "Not long before = poco antes de (close in time, but earlier)."
        },

        {
          type: "mc",
          q: "Rafael told his board: 'We need someone who can perform ___.'",
          options: ["miracles", "resources", "workshops"],
          a: "miracles",
          tip: "Miracles = milagros (used hyperbolically for extraordinary results).",
          successTip: "'Work miracles' = hacer milagros. 'She worked miracles with that class.' Very common hyperbole in education. 'I can't work miracles!' = ¡No soy mago! 🪄"
        },

        {
          type: "mc",
          q: "Rafael ___ the board that a new approach was needed. ___, they approved his budget.",
          options: ["told / Subsequently", "said / Consequently", "told / Consequently"],
          a: "told / Consequently",
          tip: "Told + object (the board). Consequently = as a result of convincing them.",
          successTip: "Reported speech + connector together! 'He told them... Consequently...' This is how professional reports and journalism actually flow! 📰"
        },

        {
          type: "mc",
          q: "___ the budget was approved, Rafael posted the job advertisement.",
          options: ["As soon as", "Subsequently", "Not long before"],
          a: "As soon as",
          tip: "As soon as = immediately after (tan pronto como)."
        },

        {
          type: "order",
          q: "Traduce: 'Poco antes de que llegara Ms. Osei, la situación era crítica.'",
          words: ["Not", "long", "before", "Ms.", "Osei", "arrived,", "the", "situation", "was", "critical."],
          a: "Not long before Ms. Osei arrived, the situation was critical.",
          extra: ["Shortly", "Subsequently", "problem"]
        },

        {
          type: "mc",
          q: "Rafael said the school had great ___. It just needed the right teacher to use them.",
          options: ["resources", "miracles", "results"],
          a: "resources",
          tip: "Resources = materiales y medios disponibles."
        },

        {
          type: "input",
          q: "Report summary: 'Three teachers resigned. ___, the school's results declined sharply.'",
          a: "Consequently",
          alts: ["As a result", "Following this", "As a consequence", "After this", "Because of this", "In turn", "For this reason", "As such"],
          successTip: "All three formal connectors used: not long before (timing), subsequently (sequence), consequently (result). Your formal English is now at C1 level! 🎓"
        }
      ]
    },

    // ===================================================================
    // SET 4: REPORTED SPEECH — PAST TENSES (B2-)
    // ===================================================================
    {
      id: "m10-u8-set4",
      title: "Set 4: The Interview",
      context: `*Accra — Ms. Osei's job interview with Rafael*

Rafael had flown to Accra specifically to interview Ms. Osei.
The interview lasted two hours.
Afterwards, Rafael called his deputy and reported everything she had said.
"She spoke about her past work with absolute clarity and confidence."

*Accra — Entrevista de trabajo de Ms. Osei con Rafael*

Rafael había volado a Accra específicamente para entrevistar a Ms. Osei.
La entrevista duró dos horas.
Después, Rafael llamó a su subdirectora y le reportó todo lo que Ms. Osei había dicho.
"Habló de su trabajo anterior con absoluta claridad y confianza."`,

      questions: [
        {
          type: "match",
          q: "Match the past tense backshift rules:",
          pairs: [
            { left: "\"I worked here for ten years.\" (Past Simple)", right: "She said she had worked there for ten years. (Past Perfect)" },
            { left: "\"I was teaching in Lagos.\" (Past Continuous)", right: "She said she had been teaching in Lagos. (Past Perfect Continuous)" },
            { left: "\"I had already run three workshops.\" (Past Perfect)", right: "She said she had already run three workshops. (no change)" }
          ],
          a: "completed",
          successTip: "Past Perfect = the end of the backshift road! It doesn't move further back. 'She had said she had run...' — Past Perfect stays Past Perfect! 🛑"
        },

        {
          type: "mc",
          q: "'I worked in Nigeria for five years.' → She said she ___ in Nigeria for five years.",
          options: ["had worked", "worked", "has worked"],
          a: "had worked",
          tip: "Past Simple → Past Perfect in reported speech."
        },

        {
          type: "mc",
          q: "'I was teaching in a rural school.' → She said she ___ in a rural school.",
          options: ["had been teaching", "was teaching", "had taught"],
          a: "had been teaching",
          tip: "Past Continuous → Past Perfect Continuous in reported speech.",
          successTip: "Past Continuous → Past Perfect Continuous! 'Was teaching' → 'had been teaching'. This is a sophisticated backshift — marks you as an advanced speaker! 🌟"
        },

        {
          type: "mc",
          q: "'I had already transformed two failing schools.' → She said she ___ already ___ two failing schools.",
          options: ["had / transformed", "has / transformed", "had / been transforming"],
          a: "had / transformed",
          tip: "Past Perfect stays Past Perfect — no further backshift needed."
        },

        {
          type: "mc",
          q: "Rafael ___ his deputy that Ms. Osei had run successful workshops in three countries.",
          options: ["told", "said", "reported"],
          a: "told",
          tip: "Told + person + that clause. Deputy = subdirector.",
          successTip: "'Deputy' = subdirector/a. Very common in schools and government. 'Deputy head' = subdirector de colegio. 'Deputy prime minister' = viceprimero ministro! 🏫"
        },

        {
          type: "mc",
          q: "'I dedicated my career to inspiring students.' → She said she ___ her career to inspiring students.",
          options: ["had dedicated", "dedicated", "was dedicating"],
          a: "had dedicated",
          tip: "Past Simple → Past Perfect in reported speech."
        },

        {
          type: "mc",
          q: "Rafael said Ms. Osei ___ the most experienced teacher he had ever interviewed.",
          options: ["was", "is", "had been"],
          a: "was",
          tip: "Was = backshifted Present Simple (she is → he said she was)."
        },

        {
          type: "order",
          q: "Traduce (reported): 'Ella dijo que había estado trabajando con recursos muy limitados durante años.'",
          words: ["She", "said", "she", "had", "been", "working", "with", "very", "limited", "resources", "for", "years."],
          a: "She said she had been working with very limited resources for years.",
          extra: ["was", "has", "worked"]
        },

        {
          type: "mc",
          q: "'I didn't give up when things were difficult.' → She said she ___ up when things ___ difficult.",
          options: ["hadn't given / had been", "didn't give / were", "hadn't given / were"],
          a: "hadn't given / had been",
          tip: "Past Simple (didn't give) → Past Perfect (hadn't given). Past Continuous (were) → Past Perfect Continuous (had been).",
          successTip: "Double backshift in one sentence! Both verbs move back. This level of accuracy in reported speech is genuinely impressive — C1 territory! 🏆"
        },

        {
          type: "input",
          q: "Rafael's report to the board: 'She said she ___ ___ teaching for over twenty years and had never stopped learning.'",
          a: "had been",
          alts: ["Had been","had been", "had kept", "had continued", "had spent", "had been doing", "had carried on", "had dedicated herself to", "had been passionate about", "had committed to", "had remained"],
          successTip: "Backshift mastered across all tenses! Present, Past Simple, Past Continuous, Present Perfect → all correctly shifted. The full picture! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 5: AT THE VERY MOMENT · JUST AS (B2-)
    // ===================================================================
    {
      id: "m10-u8-set5",
      title: "Set 5: The Workshop Crisis",
      context: `*São Paulo, three months later — Rafael's school*

Ms. Osei was running her first teacher training workshop.
Twenty teachers were sitting in the school hall, listening carefully.
At the very moment she reached the most important part of her talk,
the projector failed — and the lights went out.

*São Paulo, tres meses después — El colegio de Rafael*

Ms. Osei dirigía su primer taller de formación docente.
Veinte profesores estaban sentados en el salón del colegio, escuchando atentamente.
En el mismísimo momento en que llegó a la parte más importante de su charla,
el proyector falló — y las luces se apagaron.`,

      questions: [
        {
          type: "match",
          q: "Match the precise timing expressions:",
          pairs: [
            { left: "at the very moment", right: "en el mismísimo momento (máximo dramatismo)" },
            { left: "just as", right: "justo cuando (dos eventos simultáneos)" },
            { left: "not long before", right: "poco antes de (tiempo cercano al pasado)" }
          ],
          a: "completed",
          successTip: "'At the very moment' = dramatic emphasis version of 'just as'. 'Very' intensifies it. 'At the very moment she spoke, everything changed.' Maximum tension! 🎭"
        },

        {
          type: "mc",
          q: "___ ___ ___ she reached the key point, the projector failed.",
          options: ["At the very moment", "Just as", "Shortly after"],
          a: "At the very moment",
          tip: "At the very moment = dramatic simultaneous event (mismísimo momento).",
          successTip: "At the very moment = 'just as' but MORE dramatic. Use it for climactic narrative moments. 'At the very moment she opened the letter, the phone rang.' 🎬"
        },

        {
          type: "mc",
          q: "___ the lights went out, someone in the room laughed nervously.",
          options: ["Just as", "Subsequently", "Not long before"],
          a: "Just as",
          tip: "Just as = two things at exactly the same moment."
        },

        {
          type: "mc",
          q: "Ms. Osei didn't panic. ___, she continued speaking in the dark.",
          options: ["Subsequently", "Consequently", "Just as"],
          a: "Subsequently",
          tip: "Subsequently = what happened next (after the lights went out).",
          successTip: "'Subsequently she continued' = 'then she continued'. Subsequently sounds more formal and deliberate — perfect for describing a professional's calm response! 💼"
        },

        {
          type: "mc",
          q: "The teachers were so inspired that they forgot about the darkness. ___, the session became the best of the year.",
          options: ["Consequently", "Subsequently", "Not long before"],
          a: "Consequently",
          tip: "Consequently = as a result of the inspiration."
        },

        {
          type: "mc",
          q: "___ the workshop, Rafael had warned Ms. Osei that the projector was unreliable.",
          options: ["Not long before", "Just as", "At the very moment"],
          a: "Not long before",
          tip: "Not long before = poco antes de (he warned her shortly before it started)."
        },

        {
          type: "mc",
          q: "Rafael later told colleagues that Ms. Osei ___ working miracles from day one.",
          options: ["had been", "was", "has been"],
          a: "had been",
          tip: "Past Perfect Continuous = ongoing activity before the reporting moment.",
          successTip: "'Working miracles' = haciendo milagros. Hyperbolic but natural! 'That teacher works miracles with difficult classes.' High praise in any school! 🪄"
        },

        {
          type: "mc",
          q: "___ ___ ___ the projector failed, Ms. Osei had been speaking for forty minutes.",
          options: ["At the very moment", "As soon as", "Shortly after"],
          a: "At the very moment",
          tip: "At the very moment = dramatic simultaneous marker."
        },

        {
          type: "order",
          q: "Traduce: 'Justo cuando empezaba la parte más importante, se fue la luz.'",
          words: ["Just", "as", "the", "most", "important", "part", "was", "beginning,", "the", "lights", "went", "out."],
          a: "Just as the most important part was beginning, the lights went out.",
          extra: ["At", "moment", "started"]
        },

        {
          type: "input",
          q: "Rafael's report: '___ ___ ___ ___ she finished speaking, the teachers gave her a standing ovation.' (en el mismísimo momento en que)",
          a: "At the very moment",
          alts: ["at the very moment","As soon as", "The moment that", "The very second", "The instant that", "No sooner had", "Right after", "The very moment", "By the time", "Almost before", "The very instant"],
          successTip: "At the very moment = the pinnacle of narrative precision. A standing ovation at the exact second she finished — that's the power of precise timing language! 👏"
        }
      ]
    },

    // ===================================================================
    // SET 6: REPORTED QUESTIONS (B2-)
    // ===================================================================
    {
      id: "m10-u8-set6",
      title: "Set 6: Priya's Interview Questions",
      context: `*São Paulo — Priya interviews Ms. Osei and Rafael*

Priya has come to write a full feature on the school's transformation.
She interviews both Ms. Osei and Rafael separately.
Later, she writes up every question and answer in her notes.
"The questions I asked were simple. The answers were extraordinary."

*São Paulo — Priya entrevista a Ms. Osei y Rafael*

Priya ha venido a escribir un reportaje completo sobre la transformación del colegio.
Entrevista a Ms. Osei y a Rafael por separado.
Luego, escribe todas las preguntas y respuestas en sus notas.
"Las preguntas que hice eran simples. Las respuestas eran extraordinarias."`,

      questions: [
        {
          type: "match",
          q: "Match the reported question rules:",
          pairs: [
            { left: "\"Are you experienced?\" (Yes/No)", right: "She asked if/whether he was experienced." },
            { left: "\"What do you need?\" (WH-question)", right: "She asked what they needed." },
            { left: "\"How long have you taught?\"", right: "She asked how long she had taught." }
          ],
          a: "completed",
          successTip: "Reported questions LOSE the question mark AND use normal word order (subject + verb). 'What do you need?' → 'She asked what they needed.' NO inversion! ✅"
        },

        {
          type: "mc",
          q: "Priya asked: 'Are you happy here?' → She asked Ms. Osei ___ she was happy there.",
          options: ["if", "whether", "that"],
          a: "if",
          tip: "Yes/No questions → if or whether in reported speech.",
          successTip: "IF or WHETHER — both correct for yes/no questions! 'She asked if she was happy.' / 'She asked whether she was happy.' Whether sounds slightly more formal! 🎓"
        },

        {
          type: "mc",
          q: "'What resources does the school have?' → Priya asked what resources the school ___.",
          options: ["had", "has", "does have"],
          a: "had",
          tip: "Have (Present Simple) → had (Past Simple) in reported speech. No inversion!"
        },

        {
          type: "mc",
          q: "'How long have you been teaching?' → She asked how long Ms. Osei ___ teaching.",
          options: ["had been", "has been", "was"],
          a: "had been",
          tip: "Present Perfect Continuous → Past Perfect Continuous in reported questions."
        },

        {
          type: "mc",
          q: "'Did you always want to inspire students?' → Priya asked ___ she had always wanted to inspire students.",
          options: ["if", "what", "how"],
          a: "if",
          tip: "Yes/No question → if/whether. Did → had (Past Simple → Past Perfect).",
          successTip: "Did → had in reported questions! 'Did you want' → 'if she had wanted'. Past Simple becomes Past Perfect even in questions! Full backshift! 🔙"
        },

        {
          type: "mc",
          q: "'What workshop are you planning next?' → She asked what workshop Ms. Osei ___ next.",
          options: ["was planning", "is planning", "had planned"],
          a: "was planning",
          tip: "Present Continuous → Past Continuous. No auxiliary in reported question."
        },

        {
          type: "mc",
          q: "Priya asked Rafael ___ the school had struggled before Ms. Osei arrived.",
          options: ["whether", "what", "how"],
          a: "whether",
          tip: "Yes/No implied question (had it struggled?) → whether.",
          successTip: "'Whether' often replaces 'if' when the question implies a binary choice: yes or no. 'She asked whether the school had struggled.' More formal and precise! 📰"
        },

        {
          type: "order",
          q: "Traduce (reported question): 'Priya preguntó cuántos profesores habían asistido al taller.'",
          words: ["Priya", "asked", "how", "many", "teachers", "had", "attended", "the", "workshop."],
          a: "Priya asked how many teachers had attended the workshop.",
          extra: ["if", "whether", "attended"]
        },

        {
          type: "mc",
          q: "'Why did you choose this school?' → She asked Ms. Osei why she ___ that school.",
          options: ["had chosen", "chose", "has chosen"],
          a: "had chosen",
          tip: "Past Simple → Past Perfect. No question word order in reported speech."
        },

        {
          type: "input",
          q: "Priya's notes: 'I asked Rafael ___ he had ever considered giving up.'",
          a: "if",
          alts: ["whether", "If", "Whether"],
          successTip: "Reported questions mastered! ✅ If/whether (yes/no) + WH-word (open) + normal word order + backshift. Four rules, one structure — you've got it! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 7: REPORTED COMMANDS & REQUESTS (B2)
    // ===================================================================
    {
      id: "m10-u8-set7",
      title: "Set 7: Rafael's Training Day",
      context: `*São Paulo — a full-day teacher training workshop*

Rafael ran a training day for all forty teachers at the school.
He gave clear instructions. He made requests. He issued warnings.
"That day," Ms. Osei said later, "he told us exactly what he expected.
No one left that room without a sense of purpose."

*São Paulo — un día completo de formación docente*

Rafael dirigió un día de formación para los cuarenta profesores del colegio.
Dio instrucciones claras. Hizo peticiones. Emitió advertencias.
"Ese día," dijo Ms. Osei después, "nos dijo exactamente lo que esperaba.
Nadie salió de esa sala sin un sentido de propósito."`,

      questions: [
        {
          type: "match",
          q: "Match the reported command structures:",
          pairs: [
            { left: "\"Work harder.\" (command)", right: "He told them to work harder." },
            { left: "\"Please share your resources.\" (request)", right: "He asked them to share their resources." },
            { left: "\"Don't give up on your students.\" (negative)", right: "He told them not to give up on their students." }
          ],
          a: "completed",
          successTip: "Commands → told + person + to + infinitive. Requests → asked + person + to + infinitive. Negative → told/asked + person + NOT to + infinitive! 🎯"
        },

        {
          type: "mc",
          q: "Rafael said: 'Inspire your students every day.' → He told the teachers ___ their students every day.",
          options: ["to inspire", "inspire", "inspiring"],
          a: "to inspire",
          tip: "Command → told + person + to + infinitive.",
          successTip: "Told them TO INSPIRE = indirect command. The infinitive never changes — no tense backshift for commands! 'He told them to work' — always to + base verb! ✅"
        },

        {
          type: "mc",
          q: "Rafael said: 'Please don't cut corners with lesson planning.' → He asked them ___ corners with lesson planning.",
          options: ["not to cut", "to not cut", "don't cut"],
          a: "not to cut",
          tip: "Negative command/request → not to + infinitive (NOT to cut).",
          successTip: "'Not to cut corners' — reported negative commands always use NOT TO. 'He warned them not to cut corners.' The NOT comes BEFORE TO! ❌✂️"
        },

        {
          type: "mc",
          q: "Ms. Osei said: 'Always believe in your students.' → She ___ them always to believe in their students.",
          options: ["told", "asked", "warned"],
          a: "told",
          tip: "Direct command → told + person + to + infinitive.",
          successTip: "Told vs Asked for commands! TOLD = stronger, more direct instruction. ASKED = polite request. 'He told them to stay.' vs 'He asked them to stay.' Subtle but important! 💬"
        },

        {
          type: "mc",
          q: "Rafael said: 'Don't be afraid to ask for more resources.' → He encouraged them ___ afraid to ask for more resources.",
          options: ["not to be", "to not be", "to be not"],
          a: "not to be",
          tip: "Negative command → not to + infinitive."
        },

        {
          type: "mc",
          q: "He said: 'Please share your best ideas.' → He ___ the teachers to share their best ideas.",
          options: ["asked", "told", "warned"],
          a: "asked",
          tip: "Please = polite request → asked + person + to + infinitive.",
          successTip: "Warned = told + to + infinitive but for DANGERS. 'He warned them not to ignore struggling students.' Warned always implies a negative consequence! ⚠️"
        },

        {
          type: "mc",
          q: "Rafael warned: 'Don't ignore students who are falling behind.' → He warned the teachers ___ students who were falling behind.",
          options: ["not to ignore", "to not ignore", "don't ignore"],
          a: "not to ignore",
          tip: "Warning + negative → warned + not to + infinitive."
        },

        {
          type: "order",
          q: "Traduce (reported command): 'Él les pidió que compartieran sus recursos con los colegas nuevos.'",
          words: ["He", "asked", "them", "to", "share", "their", "resources", "with", "the", "new", "colleagues."],
          a: "He asked them to share their resources with the new colleagues.",
          extra: ["told", "sharing", "not"]
        },

        {
          type: "mc",
          q: "Ms. Osei: 'Never stop learning.' → She told all the teachers ___ stop learning.",
          options: ["never to", "not to never", "to not ever"],
          a: "never to",
          tip: "Never + command → never to + infinitive (or not to + ever).",
          successTip: "'Never to stop' = never to + infinitive. Less common than 'not to', but grammatically elegant and sounds very deliberate. Perfect for inspirational quotes! ✨"
        },

        {
          type: "input",
          q: "Workshop summary: 'Rafael asked the teachers ___ ___ in their students, even when it was difficult.'",
          a: "to believe",
          alts: ["To believe","to believe", "to always believe", "to truly believe", "to still believe", "to genuinely believe", "to keep believing", "to never stop believing", "to firmly believe", "to continue believing", "to wholeheartedly believe"],
          successTip: "Reported commands fully mastered! told/asked/warned + person + (not) to + infinitive. Simple structure, sophisticated effect. 🏫🎓"
        }
      ]
    },

    // ===================================================================
    // SET 8: CONSEQUENTLY & SUBSEQUENTLY IN CRISIS (B2)
    // ===================================================================
    {
      id: "m10-u8-set8",
      title: "Set 8: The School in Crisis",
      context: `*São Paulo, six months later — an unexpected inspection*

The school board announced an unplanned inspection — with one week's notice.
Not long before the announcement, Ms. Osei had completely redesigned the curriculum.
Subsequently, she ran three emergency preparation workshops.
Consequently, when the inspectors arrived, the school was ready.

*São Paulo, seis meses después — una inspección inesperada*

La junta escolar anunció una inspección no planificada — con una semana de aviso.
Poco antes del anuncio, Ms. Osei había rediseñado completamente el plan de estudios.
Posteriormente, impartió tres talleres de preparación de emergencia.
Por consiguiente, cuando llegaron los inspectores, el colegio estaba preparado.`,

      questions: [
        {
          type: "mc",
          q: "___ ___ ___ the inspection was announced, Ms. Osei had already restructured the curriculum.",
          options: ["Not long before", "Just as", "As soon as"],
          a: "Not long before",
          tip: "Not long before = poco antes de (close timing, but earlier)."
        },

        {
          type: "mc",
          q: "The board announced the inspection. ___, Ms. Osei called an emergency staff meeting.",
          options: ["Subsequently", "Consequently", "Not long before"],
          a: "Subsequently",
          tip: "Subsequently = what happened next (sequence without cause-effect).",
          successTip: "Subsequently vs Consequently — still tricky? SUBSEQUENTLY = next in order. CONSEQUENTLY = because of that. 'She fell. Subsequently, she cried. Consequently, she had a scar.' 🔗"
        },

        {
          type: "mc",
          q: "The teachers were well-prepared. ___, the inspection results were outstanding.",
          options: ["Consequently", "Subsequently", "Shortly after"],
          a: "Consequently",
          tip: "Outstanding results = direct consequence of being well-prepared."
        },

        {
          type: "mc",
          q: "Ms. Osei told the staff ___ panic, but ___ focus on what they knew.",
          options: ["not to / to", "to not / not to", "not to / not to"],
          a: "not to / to",
          tip: "Not to panic (negative command) + to focus (positive command)."
        },

        {
          type: "mc",
          q: "___ the inspectors arrived, Ms. Osei was giving a live demonstration lesson.",
          options: ["Just as", "Subsequently", "Consequently"],
          a: "Just as",
          tip: "Just as = two simultaneous events (arrived + was giving)."
        },

        {
          type: "mc",
          q: "The lead inspector asked Ms. Osei ___ she had developed the new curriculum herself.",
          options: ["whether", "what", "how"],
          a: "whether",
          tip: "Yes/No implied question → whether/if in reported speech."
        },

        {
          type: "mc",
          q: "Ms. Osei replied that she ___ it collaboratively with all forty teachers.",
          options: ["had developed", "developed", "has developed"],
          a: "had developed",
          tip: "Past Simple → Past Perfect in reported statement."
        },

        {
          type: "mc",
          q: "The inspector told Rafael the school ___ transformed beyond recognition.",
          options: ["had been", "has been", "was being"],
          a: "had been",
          tip: "Has been (Present Perfect) → had been (Past Perfect) in reported speech.",
          successTip: "'Transformed beyond recognition' = transformado hasta ser irreconocible. Very powerful compliment! 'The city had been transformed beyond recognition.' 🌟"
        },

        {
          type: "order",
          q: "Traduce: 'Poco antes de la inspección, Ms. Osei había impartido tres talleres de emergencia. Por consiguiente, los resultados fueron excelentes.'",
          words: ["Not", "long", "before", "the", "inspection,", "Ms.", "Osei", "had", "run", "three", "emergency", "workshops.", "Consequently,", "the", "results", "were", "excellent."],
          a: "Not long before the inspection, Ms. Osei had run three emergency workshops. Consequently, the results were excellent.",
          extra: ["Subsequently", "before", "ran"]
        },

        {
          type: "input",
          q: "Inspection report: 'The teachers said they ___ ___ inspired by Ms. Osei's approach.'",
          a: "had been",
          alts: ["Had been","had been", "felt deeply", "had felt", "were truly", "had always", "were genuinely", "had remained", "were constantly", "had grown", "were deeply"],
          successTip: "Passive reported speech! 'They said they had been inspired.' Passive + reported speech + backshift = three grammar elements in one sentence. Elite English! 🏆"
        }
      ]
    },

    // ===================================================================
    // SET 9: MIXED REPORTING VERBS (B2+)
    // ===================================================================
    {
      id: "m10-u8-set9",
      title: "Set 9: The Annual Conference",
      context: `*São Paulo — end-of-year education conference*

Ms. Osei and Rafael were invited to speak at a national education conference.
Yuki had also come — now training as a teacher herself, inspired by Ms. Osei.
Different people said different things, in different ways.
Priya was there to report every word.

*São Paulo — conferencia educativa de fin de año*

Ms. Osei y Rafael fueron invitados a hablar en una conferencia nacional de educación.
Yuki también había venido — ahora en formación como profesora ella misma, inspirada por Ms. Osei.
Distintas personas dijeron distintas cosas, de distintas maneras.
Priya estaba allí para reportar cada palabra.`,

      questions: [
        {
          type: "match",
          q: "Match the reporting verbs to their meaning and structure:",
          pairs: [
            { left: "admitted", right: "admitió (confesó algo difícil de decir)" },
            { left: "promised", right: "prometió (comprometerse a algo)" },
            { left: "suggested", right: "sugirió (propuso una idea)" }
          ],
          a: "completed",
          successTip: "Reporting verbs carry MEANING! Said = neutral. Admitted = reluctant truth. Promised = commitment. Suggested = idea. Choose carefully — they change the whole tone! 🎭"
        },

        {
          type: "mc",
          q: "Ms. Osei ___ that teaching without adequate resources was the greatest challenge she had faced.",
          options: ["admitted", "promised", "insisted"],
          a: "admitted",
          tip: "Admitted = acknowledged something difficult or honest.",
          successTip: "Admitted that + clause. Also: admitted + -ing. 'She admitted feeling overwhelmed.' Both work! Admitted = she said it even though it was hard to say! 💬"
        },

        {
          type: "mc",
          q: "Rafael ___ that every school in the country deserved the same resources.",
          options: ["insisted", "admitted", "suggested"],
          a: "insisted",
          tip: "Insisted = said firmly and repeatedly (that + clause).",
          successTip: "Insisted that + Past = past truth. Insisted on + gerund = insisted on receiving. Two different structures! 'He insisted it was wrong.' / 'He insisted on being heard.' 💪"
        },

        {
          type: "mc",
          q: "Yuki ___ that smaller class sizes might help struggling students.",
          options: ["suggested", "warned", "promised"],
          a: "suggested",
          tip: "Suggested = proposed an idea (that + clause or -ing).",
          successTip: "Suggested that + Past (NOT would). 'She suggested that they tried smaller classes.' ✅ Common mistake: 'She suggested that they should try.' Also ok, but longer! 🎓"
        },

        {
          type: "mc",
          q: "Ms. Osei ___ the audience not to underestimate their students' potential.",
          options: ["urged", "suggested", "admitted"],
          a: "urged",
          tip: "Urged = strongly encouraged (+ person + to + infinitive).",
          successTip: "Urged + person + to + infinitive = strongly told. 'She urged them to believe.' More powerful than 'told' — implies passion and importance! 🔥"
        },

        {
          type: "mc",
          q: "Rafael ___ to continue funding teacher training workshops for the next three years.",
          options: ["promised", "insisted", "warned"],
          a: "promised",
          tip: "Promised = committed to something (+ to + infinitive or + that clause).",
          successTip: "Promised to + infinitive = commitment about own actions. 'He promised to fund it.' Promised that + clause = commitment about facts. 'He promised it would happen.' Both natural! 🤝"
        },

        {
          type: "mc",
          q: "Priya ___ Yuki that her question to the panel had been the highlight of the session.",
          options: ["told", "said", "admitted"],
          a: "told",
          tip: "Told + person + that clause. Said doesn't take a person object."
        },

        {
          type: "order",
          q: "Traduce: 'Ella admitió que había sido difícil al principio, pero insistió en que valía la pena.'",
          words: ["She", "admitted", "it", "had", "been", "difficult", "at", "first,", "but", "insisted", "it", "was", "worth", "it."],
          a: "She admitted it had been difficult at first, but insisted it was worth it.",
          extra: ["said", "had", "worth"]
        },

        {
          type: "mc",
          q: "Ms. Osei ___ Yuki to keep going when things felt impossible in her first teaching year.",
          options: ["encouraged", "insisted", "suggested"],
          a: "encouraged",
          tip: "Encouraged + person + to + infinitive = gave support and confidence.",
          successTip: "Encouraged = motivated someone. 'She encouraged them to keep going.' More positive than 'told' — implies belief in the person! Perfect for education contexts! 💛"
        },

        {
          type: "input",
          q: "Conference report: 'Rafael ___ that the school would never have recovered without Ms. Osei's dedication.'",
          a: "admitted",
          alts: ["Admitted","stated", "claimed", "argued", "insisted", "declared", "confirmed", "emphasized", "maintained", "stressed", "acknowledged"],
          successTip: "Rich reporting verbs complete! Admitted, insisted, suggested, urged, promised, encouraged — each one adds colour and meaning beyond plain 'said'. That's sophisticated English! 🌈"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL NARRATIVE — ALL ELEMENTS (B2+)
    // ===================================================================
    {
      id: "m10-u8-set10",
      title: "Set 10: The Award",
      context: `*São Paulo — National Teaching Excellence Award ceremony*

Not long before the ceremony, Ms. Osei had insisted she didn't deserve an award.
Subsequently, when her name was called, she stood very still for a moment.
Just as she was walking to the stage, Yuki started crying.
At the very moment she reached the microphone, the room fell completely silent.

*São Paulo — Ceremonia del Premio Nacional a la Excelencia Docente*

Poco antes de la ceremonia, Ms. Osei había insistido en que no merecía un premio.
Posteriormente, cuando dijeron su nombre, se quedó completamente quieta un momento.
Justo cuando se dirigía al escenario, Yuki empezó a llorar.
En el mismísimo momento en que llegó al micrófono, la sala quedó completamente en silencio.`,

      questions: [
        {
          type: "mc",
          q: "___ ___ ___ the ceremony, Ms. Osei had insisted she didn't deserve an award.",
          options: ["Not long before", "Just as", "As soon as"],
          a: "Not long before",
          tip: "Not long before = poco antes de (close but earlier in time)."
        },

        {
          type: "mc",
          q: "___ her name was called, she walked calmly to the stage.",
          options: ["As soon as", "Subsequently", "Not long before"],
          a: "As soon as",
          tip: "As soon as = immediate action following the announcement."
        },

        {
          type: "mc",
          q: "___ ___ ___ she reached the microphone, the room fell silent.",
          options: ["At the very moment", "Just as", "Shortly after"],
          a: "At the very moment",
          tip: "At the very moment = maximum dramatic simultaneity.",
          successTip: "Three timing adverbs in one story! Not long before (earlier), as soon as (immediate), at the very moment (dramatic simultaneous). Perfect narrative rhythm! 🎬"
        },

        {
          type: "mc",
          q: "Ms. Osei ___ that she was accepting the award on behalf of every teacher in the room.",
          options: ["said", "told", "insisted"],
          a: "said",
          tip: "Said + that clause (no specific person object needed)."
        },

        {
          type: "mc",
          q: "She ___ that real miracles happened in classrooms every single day — if teachers were given the resources they needed.",
          options: ["insisted", "admitted", "promised"],
          a: "insisted",
          tip: "Insisted = said firmly and with conviction.",
          successTip: "Insisted = perfect for passionate convictions. Ms. Osei isn't just saying it — she INSISTS. The reporting verb tells us HOW she said it! 💪"
        },

        {
          type: "mc",
          q: "She ___ the audience to remember why they had chosen teaching in the first place.",
          options: ["urged", "warned", "told"],
          a: "urged",
          tip: "Urged = strongly encouraged (passionate appeal)."
        },

        {
          type: "mc",
          q: "She ___ not to give up when students were difficult, because those students needed them most.",
          options: ["warned them", "told them", "asked them"],
          a: "warned them",
          tip: "Warned + person + not to + infinitive = alert about a danger/risk.",
          successTip: "Warned them not to = the negative command reporting verb for danger. 'She warned them not to give up' implies something bad would happen if they did! ⚠️"
        },

        {
          type: "mc",
          q: "Rafael was sitting in the front row. ___, he had been the one who had believed in her first.",
          options: ["After all", "Subsequently", "Just as"],
          a: "After all",
          tip: "After all = al fin y al cabo (used to acknowledge a truth).",
          successTip: "'After all' = al fin y al cabo / después de todo. Different from 'subsequently'! After all = acknowledging something already true. Very natural in English! 💡"
        },

        {
          type: "order",
          q: "Traduce: 'Ella admitió que nunca había imaginado estar allí, pero prometió continuar inspirando estudiantes.'",
          words: ["She", "admitted", "she", "had", "never", "imagined", "being", "there,", "but", "promised", "to", "continue", "inspiring", "students."],
          a: "She admitted she had never imagined being there, but promised to continue inspiring students.",
          extra: ["said", "insisting", "have"]
        },

        {
          type: "input",
          q: "Priya's final sentence: At the very moment she ___ speaking, the audience rose to its feet.",
          a: "finished",
          alts: ["stopped", "completed", "ended", "concluded", "quit"],
          successTip: "🏆 Rose to its feet = Ovacionar de pie! 📰🌍"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u8-reading",
    title: "Reading: In Their Own Words",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**Ms. Osei — The First Week**

As soon as Ms. Osei arrived at the school, she noticed the lack of resources. She told Rafael that the classrooms needed new materials immediately. Not long before her arrival, three experienced teachers had resigned. Consequently, morale was very low.`
      },
      {
        type: "mc",
        q: "What did Ms. Osei notice as soon as she arrived?",
        options: ["The lack of resources", "The small classrooms", "The large number of students"],
        a: "The lack of resources",
        tip: "'...she noticed the lack of resources.'"
      },
      {
        type: "mc",
        q: "Why was morale low?",
        options: ["Three experienced teachers had resigned", "The inspection had gone badly", "Rafael had cut the budget"],
        a: "Three experienced teachers had resigned",
        tip: "'...three experienced teachers had resigned. Consequently, morale was very low.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**The Workshop That Changed Everything**

At the very moment Ms. Osei reached the key part of her talk, the projector failed. Subsequently, she continued speaking in the dark. She urged the teachers not to rely on technology. Consequently, the session became the most memorable of the year.`
      },
      {
        type: "mc",
        q: "What happened at the very moment she reached the key part?",
        options: ["The projector failed", "The lights came back on", "The teachers left"],
        a: "The projector failed",
        tip: "'At the very moment Ms. Osei reached the key part...the projector failed.'"
      },
      {
        type: "mc",
        q: "What did Ms. Osei urge the teachers to do?",
        options: ["Not to rely on technology", "To ask for more resources", "To inspire their students more"],
        a: "Not to rely on technology",
        tip: "'She urged the teachers not to rely on technology.'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**Priya's Interview Notes**

Priya asked Ms. Osei whether she had always wanted to be a teacher. Ms. Osei admitted that she had originally trained as an engineer. She said she had changed careers after a student she had mentored went on to win a national science prize.`
      },
      {
        type: "mc",
        q: "What did Priya ask Ms. Osei?",
        options: ["Whether she had always wanted to be a teacher", "What resources she needed", "How long she had been teaching"],
        a: "Whether she had always wanted to be a teacher",
        tip: "'Priya asked Ms. Osei whether she had always wanted to be a teacher.'"
      },
      {
        type: "mc",
        q: "What had Ms. Osei originally trained as?",
        options: ["An engineer", "A journalist", "An architect"],
        a: "An engineer",
        tip: "'She admitted that she had originally trained as an engineer.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**Rafael's Turning Point**

Not long before the inspection, Rafael told his staff to stay calm. He promised the board that results would improve. Just as the inspectors arrived, Ms. Osei was demonstrating a live lesson. Subsequently, the lead inspector called it the best teaching he had ever observed.`
      },
      {
        type: "mc",
        q: "What did Rafael promise the board?",
        options: ["That results would improve", "That he would hire more teachers", "That the school would get new resources"],
        a: "That results would improve",
        tip: "'He promised the board that results would improve.'"
      },
      {
        type: "mc",
        q: "What was Ms. Osei doing just as the inspectors arrived?",
        options: ["Demonstrating a live lesson", "Running a workshop", "Speaking to Rafael"],
        a: "Demonstrating a live lesson",
        tip: "'Just as the inspectors arrived, Ms. Osei was demonstrating a live lesson.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**The Award Speech**

At the very moment Ms. Osei's name was announced, Yuki began to cry. Ms. Osei insisted the award belonged to every teacher in the room. She urged them never to give up on their students. Subsequently, the entire audience rose to its feet.`
      },
      {
        type: "mc",
        q: "Who began to cry when Ms. Osei's name was announced?",
        options: ["Yuki", "Priya", "Rafael"],
        a: "Yuki",
        tip: "'...Yuki began to cry.'"
      },
      {
        type: "mc",
        q: "What did Ms. Osei insist about the award?",
        options: ["That it belonged to every teacher in the room", "That she didn't deserve it", "That it should be given to Rafael"],
        a: "That it belonged to every teacher in the room",
        tip: "'Ms. Osei insisted the award belonged to every teacher in the room.'",
        successTip: "A school transformed through dedication, workshops, resources, and inspiration — all told through reported speech and precise time adverbs. Unit 8 complete! 🌟🏫"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u8-review",
    title: "Unit 8 Review: Reported Speech & Time Adverbs",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: ___ she walked in, the room went quiet.",
        options: ["Just as", "Subsequently", "Consequently"],
        a: "Just as",
        tip: "Just as = justo cuando (simultaneous events)."
      },
      {
        type: "mc",
        q: "Review: 'I love teaching.' → She said she ___ teaching.",
        options: ["loved", "loves", "had loved"],
        a: "loved",
        tip: "Present Simple → Past Simple in reported speech."
      },
      {
        type: "mc",
        q: "Review: Three teachers left. ___, results dropped.",
        options: ["Consequently", "Subsequently", "Not long before"],
        a: "Consequently",
        tip: "Consequently = as a direct result of (cause → effect)."
      },
      {
        type: "mc",
        q: "Review: 'Are you experienced?' → She asked ___ he was experienced.",
        options: ["if", "that", "what"],
        a: "if",
        tip: "Yes/No question → if/whether in reported speech."
      },
      {
        type: "mc",
        q: "Review: 'Work harder.' → He told them ___ harder.",
        options: ["to work", "work", "working"],
        a: "to work",
        tip: "Command → told + person + to + infinitive."
      },
      {
        type: "mc",
        q: "Review: 'Don't give up.' → She told them ___ give up.",
        options: ["not to", "to not", "don't"],
        a: "not to",
        tip: "Negative command → not to + infinitive."
      },
      {
        type: "mc",
        q: "Review: She ___ that teaching had been the best decision of her life.",
        options: ["admitted", "told", "urged"],
        a: "admitted",
        tip: "Admitted = acknowledged a personal truth (+ that clause)."
      },
      {
        type: "mc",
        q: "Review: ___ ___ ___ the inspection, Ms. Osei had redesigned the curriculum.",
        options: ["Not long before", "Just as", "As soon as"],
        a: "Not long before",
        tip: "Not long before = poco antes de (close but earlier)."
      },
      {
        type: "mc",
        q: "Review: 'I worked here for ten years.' → She said she ___ there for ten years.",
        options: ["had worked", "worked", "has worked"],
        a: "had worked",
        tip: "Past Simple → Past Perfect in reported speech."
      },
      {
        type: "mc",
        q: "Review: She ___ the teachers to believe in every student, no matter how difficult.",
        options: ["urged", "said", "suggested"],
        a: "urged",
        tip: "Urged = strongly encouraged (+ person + to + infinitive).",
        successTip: "🎉 UNIT 8 MASTERED! Time adverbs ✅ Reported statements ✅ Reported questions ✅ Reported commands ✅ Rich verbs ✅ Ready for Unit 9! 🚀📰"
      }
    ]
  }
};
